import { useId, useRef, type ReactNode } from "react";
import { motion, useScroll, useTransform, type MotionValue } from "framer-motion";

/**
 * A pinned deck of cards that get swiped off to the right (Tinder-style) as you scroll, each one getting a
 * teal glow on the way out. The last card stays so the section ends on a full card.
 *
 * Primary path: CSS scroll-driven animations (compositor-driven, so they stay smooth).
 * Fallback: the same motion driven from JS for browsers that don't support them yet.
 */

const TEAL = "#17a1a8";
const TEAL_LIGHT = "#3dc7cc";

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

// Estimated height of the pinned panel (heading + deck); the pin releases when the track's bottom reaches it
const PANEL_PX = 700;
const CARD_BOX = "aspect-[3/4] h-[min(430px,50svh)]";
const PIN_TOP_PX = 80;
// "fit" mode: the pinned frame is exactly the visible area between the header and the floating CTA bar, with its content centred
const FIT_RESERVE_PX = PIN_TOP_PX + 96;

interface PinnedCardStackProps {
    count: number;
    /** Heading content shown above the deck while pinned */
    header: ReactNode;
    /** Face of card `index`; it fills the portrait card */
    renderCard: (index: number) => ReactNode;
    /** Scroll fractions (0-1 of the pinned distance) where each card but the last leaves */
    exitRanges: [number, number][];
    /** Track height in viewport heights: more = slower swipe */
    trackVh: number;
    /** Estimated height of the pinned panel (heading + deck) in px */
    panelPx?: number;
    /** Size classes of the card box (default: portrait) */
    cardClass?: string;
    /** Centre the content in the visible area (between the header and the floating CTA) instead of pinning it to the top */
    fit?: boolean;
}

// Cards deeper in the pile sit slightly off-axis; the front card is straight
const tilt = (rank: number) => (rank === 0 ? 0 : rank % 2 ? 2 : -2);

// Every deck on the page injects its own rules, so they are scoped to the deck's own class (`u`) to keep decks from overriding each other
const buildCss = (u: string, count: number, ranges: [number, number][], trackVh: number, panelPx: number, fit: boolean) => {
    const start = (j: number) => `cover calc(var(--pcs-base) + ${ranges[j][0]} * var(--pcs-dist))`;
    const end = (j: number) => `cover calc(var(--pcs-base) + ${ranges[j][1]} * var(--pcs-dist))`;
    let css = `
.${u}.pcs-track { view-timeline: --pcs block; --pcs-base: calc(100vh - ${PIN_TOP_PX}px); --pcs-dist: calc(${trackVh}vh - ${fit ? `(100svh - ${FIT_RESERVE_PX}px)` : `${panelPx}px`}); }
.pcs-card, .pcs-exit, .pcs-glow { animation-timeline: --pcs; animation-timing-function: linear; }
.pcs-card, .pcs-exit { will-change: transform; }
@keyframes pcs-exit  { to { translate: 900px -36px; rotate: 20deg; } }
@keyframes pcs-glow  { 0% { opacity: 0; } 25% { opacity: 1; } 100% { opacity: .85; } }
`;
    for (let k = 1; k < count; k++) {
        css += `@keyframes pcs-r${k} { from { translate: 0 ${k * 16}px; scale: ${1 - k * 0.05}; rotate: ${tilt(k)}deg; } to { translate: 0 ${(k - 1) * 16}px; scale: ${1 - (k - 1) * 0.05}; rotate: ${tilt(k - 1)}deg; } }\n`;
    }
    for (let i = 0; i < count; i++) {
        if (i > 0) {
            // moves up one place in the pile each time a card in front of it leaves
            const names = Array.from({ length: i }, (_, j) => `pcs-r${i - j}`).join(", ");
            const fills = Array.from({ length: i }, (_, j) => (j === 0 ? "both" : "forwards")).join(", ");
            const rs = Array.from({ length: i }, (_, j) => `${start(j)} ${end(j)}`).join(", ");
            css += `.${u} .pcs-c${i} { animation-name: ${names}; animation-fill-mode: ${fills}; animation-range: ${rs}; }\n`;
        }
        if (i < count - 1) {
            css += `.${u} .pcs-c${i} .pcs-exit { animation-name: pcs-exit; animation-fill-mode: both; animation-range: ${start(i)} ${end(i)}; }\n`;
            css += `.${u} .pcs-c${i} .pcs-glow { animation-name: pcs-glow; animation-fill-mode: both; animation-range: ${start(i)} ${end(i)}; }\n`;
        }
    }
    return css;
};

function CardShell({
    children,
    glow,
    glowClass = "pcs-glow",
}: {
    children: ReactNode;
    glow?: MotionValue<number>;
    glowClass?: string;
}) {
    return (
        <div className="relative h-full w-full">
            {/* soft teal halo behind the card */}
            <motion.div
                style={glow ? { opacity: glow } : undefined}
                className={`${glowClass} pointer-events-none absolute -inset-3 rounded-[2.25rem] opacity-0 blur-2xl`}
            >
                <div className="h-full w-full rounded-[2.25rem]" style={{ background: `linear-gradient(135deg, ${TEAL}, ${TEAL_LIGHT})` }} />
            </motion.div>
            {children}
            {/* teal outline */}
            <motion.div
                style={glow ? { opacity: glow } : undefined}
                className={`${glowClass} pointer-events-none absolute inset-0 rounded-3xl border-2 opacity-0`}
            />
        </div>
    );
}

/* ---------- fallback ---------- */

function JsCard({
    index,
    count,
    ranges,
    progress,
    children,
}: {
    index: number;
    count: number;
    ranges: [number, number][];
    progress: MotionValue<number>;
    children: ReactNode;
}) {
    const exitP = (p: number, j: number) => (ranges[j] ? clamp01((p - ranges[j][0]) / (ranges[j][1] - ranges[j][0])) : 0);
    const rank = (p: number) => Math.max(0, index - ranges.reduce((sum, _, j) => sum + exitP(p, j), 0));
    const x = useTransform(progress, (p) => exitP(p, index) * 900);
    const y = useTransform(progress, (p) => rank(p) * 16 - exitP(p, index) * 36);
    const scale = useTransform(progress, (p) => 1 - rank(p) * 0.05);
    const rotate = useTransform(progress, (p) => exitP(p, index) * 20 + (rank(p) > 0 ? (Math.round(rank(p)) % 2 ? 2 : -2) : 0));
    const glow = useTransform(progress, (p) => Math.min(1, exitP(p, index) * 4) * (1 - exitP(p, index) * 0.15));
    void count;
    return (
        <motion.div style={{ x, y, scale, rotate, zIndex: count - index }} className="absolute inset-0">
            <CardShell glow={glow} glowClass="pcs-js-glow">
                {children}
            </CardShell>
        </motion.div>
    );
}

const FALLBACK_CSS = `
.pcs-js-glow { border-color: ${TEAL_LIGHT}; }
`;

function FramerStack({ count, header, renderCard, exitRanges, trackVh, panelPx = PANEL_PX, cardClass = CARD_BOX, fit = false }: PinnedCardStackProps) {
    const trackRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: trackRef, offset: [`start ${PIN_TOP_PX}px`, `end ${PIN_TOP_PX + (fit && typeof window !== "undefined" ? window.innerHeight - FIT_RESERVE_PX : panelPx)}px`] });
    return (
        <div ref={trackRef} className="relative" style={{ height: `${trackVh}vh` }}>
            <style>{FALLBACK_CSS}</style>
            <div className={`sticky top-20 flex flex-col items-center overflow-x-clip px-4 ${fit ? "h-[calc(100svh-11rem)] justify-center gap-5" : "gap-6 pb-6 pt-4 md:gap-8 md:pb-10 md:pt-6"}`}>
                {header}
                <div className={`relative mx-auto ${cardClass}`}>
                    {Array.from({ length: count }, (_, i) => (
                        <JsCard key={i} index={i} count={count} ranges={exitRanges} progress={scrollYProgress} >
                            {renderCard(i)}
                        </JsCard>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ---------- primary ---------- */

function CssStack({ count, header, renderCard, exitRanges, trackVh, panelPx = PANEL_PX, cardClass = CARD_BOX, fit = false }: PinnedCardStackProps) {
    const uid = `pcs-${useId().replace(/:/g, "")}`;
    return (
        <div className={`pcs-track ${uid} relative`} style={{ height: `${trackVh}vh` }}>
            <style>{buildCss(uid, count, exitRanges, trackVh, panelPx, fit)}</style>
            <style>{`.pcs-glow { border-color: ${TEAL_LIGHT}; }`}</style>
            <div className={`sticky top-20 flex flex-col items-center overflow-x-clip px-4 ${fit ? "h-[calc(100svh-11rem)] justify-center gap-5" : "gap-6 pb-6 pt-4 md:gap-8 md:pb-10 md:pt-6"}`}>
                {header}
                <div className={`relative mx-auto ${cardClass}`}>
                    {Array.from({ length: count }, (_, i) => (
                        // outer = place in the pile, inner = being swiped away (separate elements so the animations don't clash)
                        <div key={i} className={`pcs-card pcs-c${i} absolute inset-0`} style={{ zIndex: count - i }}>
                            <div className="pcs-exit h-full w-full">
                                <CardShell>{renderCard(i)}</CardShell>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function PinnedCardStack(props: PinnedCardStackProps) {
    const scrollDriven = typeof CSS !== "undefined" && CSS.supports("animation-timeline: view()");
    return scrollDriven ? <CssStack {...props} /> : <FramerStack {...props} />;
}

export default PinnedCardStack;
